import { readUser } from '../../services/session';
/**
 * GovConstructionJobDetailPage — post-award construction job detail.
 * 20 stages, per-stage mandatory checklists (hard-block on advance),
 * plus sub-views for Documents, Financials/Pay Apps, Daily Logs,
 * Personnel, Punch List, RFIs, Change Orders, Submittals,
 * Certified Payroll, Contacts.
 *
 * Route: /gov/construction/:id
 */
import { DocumentsTab } from './GovSolicitationDetailPage';
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import api from '../../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Stages + checklists (mirror backend, kept client-side for UX gating)
// ─────────────────────────────────────────────────────────────────────────────
const STAGES: { key: string; label: string; color: string }[] = [
  { key: 'award_received', label: 'Award Received', color: '#3B82F6' },
  { key: 'contract_execution', label: 'Contract Execution', color: '#3B82F6' },
  { key: 'bonds_insurance', label: 'Bonds & Insurance', color: '#4F46E5' },
  { key: 'pre_construction_planning', label: 'Pre-Construction Planning', color: '#4F46E5' },
  { key: 'kickoff_meeting', label: 'Kickoff Meeting', color: '#6366F1' },
  { key: 'submittals_approvals', label: 'Submittals & Approvals', color: '#7C3AED' },
  { key: 'baseline_schedule', label: 'Baseline Schedule', color: '#7C3AED' },
  { key: 'safety_plan', label: 'Safety Plan', color: '#8B5CF6' },
  { key: 'notice_to_proceed', label: 'Notice to Proceed', color: '#A855F7' },
  { key: 'mobilization', label: 'Mobilization', color: '#EC4899' },
  { key: 'pre_construction_conference', label: 'Pre-Con Conference', color: '#EC4899' },
  { key: 'active_construction', label: 'Active Construction', color: '#EF4444' },
  { key: 'substantial_completion', label: 'Substantial Completion', color: '#F97316' },
  { key: 'punch_list', label: 'Punch List', color: '#F97316' },
  { key: 'final_inspection', label: 'Final Inspection', color: '#F59E0B' },
  { key: 'closeout_documents', label: 'Closeout Documents', color: '#F59E0B' },
  { key: 'final_pay_application', label: 'Final Pay Application', color: '#EAB308' },
  { key: 'retention_release', label: 'Retention Release', color: '#84CC16' },
  { key: 'warranty_period', label: 'Warranty Period', color: '#22C55E' },
  { key: 'contract_closed', label: 'Contract Closed', color: '#10B981' },
];

const CHECKLISTS: Record<string, { key: string; label: string }[]> = {
  award_received: [
    { key: 'award_notice_attached', label: 'Award notice attached' },
    { key: 'contract_number_logged', label: 'Contract # logged' },
    { key: 'contract_type_set', label: 'Contract type (FFP/T&M/CPFF) set' },
    { key: 'pop_confirmed', label: 'PoP dates confirmed' },
    { key: 'co_cor_logged', label: 'CO + COR contacts logged' },
  ],
  contract_execution: [
    { key: 'contract_signed', label: 'Contract signed by both parties' },
    { key: 'executed_contract_stored', label: 'Executed contract PDF stored' },
    { key: 'pop_start_confirmed', label: 'PoP start date confirmed' },
  ],
  bonds_insurance: [
    { key: 'performance_bond', label: 'Performance bond posted' },
    { key: 'payment_bond', label: 'Payment bond posted' },
    { key: 'insurance_certs', label: 'Insurance certs uploaded (GL/WC/auto/umbrella)' },
    { key: 'gov_acknowledged', label: 'Government acknowledgment received' },
  ],
  pre_construction_planning: [
    { key: 'pm_assigned', label: 'PM assigned' },
    { key: 'super_assigned', label: 'Superintendent assigned' },
    { key: 'kickoff_scheduled', label: 'Kickoff meeting scheduled' },
    { key: 'sub_list_drafted', label: 'Subcontractor list drafted' },
    { key: 'procurement_plan', label: 'Procurement plan drafted' },
  ],
  kickoff_meeting: [
    { key: 'meeting_held', label: 'Meeting held with CO/COR' },
    { key: 'minutes_recorded', label: 'Minutes recorded' },
    { key: 'action_items_logged', label: 'Action items logged' },
    { key: 'gov_pocs_confirmed', label: 'Government POCs confirmed' },
  ],
  submittals_approvals: [
    { key: 'register_created', label: 'Submittal register created' },
    { key: 'submittals_uploaded', label: 'Submittals uploaded' },
    { key: 'status_tracked', label: 'Submittal status being tracked' },
    { key: 'long_lead_identified', label: 'Long-lead items identified' },
  ],
  baseline_schedule: [
    { key: 'cpm_uploaded', label: 'CPM schedule uploaded' },
    { key: 'baseline_approved', label: 'Baseline approved by gov' },
    { key: 'milestones_logged', label: 'Milestones logged' },
    { key: 'critical_path_documented', label: 'Critical path documented' },
  ],
  safety_plan: [
    { key: 'sssp_uploaded', label: 'Site-specific safety plan uploaded' },
    { key: 'gov_approved_safety', label: 'Gov-approved safety plan' },
    { key: 'competent_person_named', label: 'Competent person named' },
    { key: 'em385_confirmed', label: 'EM 385-1-1 confirmed (if USACE) or N/A' },
  ],
  notice_to_proceed: [
    { key: 'ntp_attached', label: 'NTP received & attached' },
    { key: 'start_date_logged', label: 'Official start date logged' },
    { key: 'pop_clock_started', label: 'PoP clock started' },
  ],
  mobilization: [
    { key: 'trailer_set', label: 'Site trailer set' },
    { key: 'utilities_connected', label: 'Utilities connected' },
    { key: 'staging_established', label: 'Staging area established' },
    { key: 'signage_installed', label: 'Signage installed' },
    { key: 'crews_deployed', label: 'Initial crews deployed' },
    { key: 'photos_uploaded', label: 'Mobilization photos uploaded' },
  ],
  pre_construction_conference: [
    { key: 'conference_held', label: 'On-site conference held' },
    { key: 'minutes_distributed', label: 'Minutes distributed' },
    { key: 'site_conditions_documented', label: 'Site conditions documented' },
  ],
  active_construction: [
    { key: 'daily_logs_active', label: 'Daily logs being maintained' },
    { key: 'progress_meetings', label: 'Weekly progress meetings scheduled' },
    { key: 'photos_continuous', label: 'Photos being uploaded continuously' },
  ],
  substantial_completion: [
    { key: 'beneficial_occupancy', label: 'Beneficial occupancy achieved' },
    { key: 'walkthrough_complete', label: 'Gov walk-through complete' },
    { key: 'punch_list_issued', label: 'Punch list issued & attached' },
  ],
  punch_list: [
    { key: 'all_items_logged', label: 'All punch items logged' },
    { key: 'evidence_uploaded', label: 'Completion evidence uploaded' },
    { key: 'reinspection_scheduled', label: 'Gov re-inspection scheduled' },
  ],
  final_inspection: [
    { key: 'final_walkthrough', label: 'Final walk-through complete' },
    { key: 'punch_signed_off', label: 'Punch list signed off' },
    { key: 'acceptance_letter', label: 'Final acceptance letter received' },
  ],
  closeout_documents: [
    { key: 'as_builts', label: 'As-builts submitted' },
    { key: 'o_and_m', label: 'O&M manuals submitted' },
    { key: 'warranty_docs', label: 'Warranty documents submitted' },
    { key: 'red_lines', label: 'Red-line drawings delivered' },
    { key: 'training', label: 'Training complete (or N/A)' },
  ],
  final_pay_application: [
    { key: 'final_pay_submitted', label: 'Final pay app submitted with backup' },
    { key: 'releases_collected', label: 'Releases of lien collected' },
    { key: 'final_invoice_sent', label: 'Final invoice sent' },
  ],
  retention_release: [
    { key: 'retention_received', label: 'Retention $ received' },
    { key: 'releases_signed', label: 'All releases signed' },
    { key: 'accounting_closed', label: 'Final accounting closed' },
  ],
  warranty_period: [
    { key: 'warranty_clock_started', label: '1-year warranty clock started' },
    { key: 'callouts_tracked', label: 'Warranty callouts tracked' },
    { key: 'end_date_logged', label: 'Warranty end date logged' },
  ],
  contract_closed: [
    { key: 'file_archived', label: 'Contract file archived' },
    { key: 'cpars_captured', label: 'Past performance record captured (CPARS)' },
    { key: 'lessons_learned', label: 'Lessons learned documented' },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
type Job = {
  id: string;
  stage: string;
  checklist_state?: Record<string, Record<string, boolean>>;
  contract_number?: string | null;
  task_order_number?: string | null;
  contract_type?: string | null;
  award_amount?: number | null;
  award_date?: string | null;
  period_of_performance_start?: string | null;
  period_of_performance_end?: string | null;
  ntp_received_at?: string | null;
  substantial_completion_date?: string | null;
  final_acceptance_date?: string | null;
  warranty_start_date?: string | null;
  warranty_end_date?: string | null;
  job_site_address?: string | null;
  contracting_officer_name?: string | null;
  contracting_officer_email?: string | null;
  contracting_officer_phone?: string | null;
  cor_name?: string | null;
  cor_email?: string | null;
  cor_phone?: string | null;
  invoiced_to_date?: number | null;
  paid_to_date?: number | null;
  retention_held?: number | null;
  percent_complete?: number | null;
  project_manager_id?: string | null;
  assigned_super_id?: string | null;
  assigned_foreman_id?: string | null;
};

type Solicitation = { id: string; title?: string; agency_name?: string };
type Activity = {
  id: string; entity_type: string; entity_id: string; actor_id?: string;
  action: string; from_stage?: string | null; to_stage?: string | null;
  message?: string | null; metadata?: any; created_at: string;
};

type SubView =
  | 'overview' | 'photos' | 'documents' | 'financials' | 'daily_logs'
  | 'personnel' | 'punch_list' | 'rfis' | 'change_orders' | 'submittals'
  | 'certified_payroll' | 'contacts';

const SUBVIEWS: { key: SubView; icon: string; label: string }[] = [
  { key: 'overview', icon: '📋', label: 'Overview' },
  { key: 'documents', icon: '📄', label: 'Documents' },
  { key: 'financials', icon: '💰', label: 'Financials' },
  { key: 'daily_logs', icon: '📅', label: 'Daily Logs' },
  { key: 'personnel', icon: '👥', label: 'Personnel' },
  { key: 'punch_list', icon: '✔️', label: 'Punch List' },
  { key: 'rfis', icon: '🔄', label: 'RFIs' },
  { key: 'change_orders', icon: '📝', label: 'Change Orders' },
  { key: 'submittals', icon: '✅', label: 'Submittals' },
  { key: 'certified_payroll', icon: '💵', label: 'Certified Payroll' },
  { key: 'contacts', icon: '📞', label: 'Contacts' },
  { key: 'photos', icon: '📸', label: 'Photos' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Styles (dark theme, matching other gov pages)
// ─────────────────────────────────────────────────────────────────────────────
// Style helpers (functions) — keep out of the record so TS keeps them callable
const styleAdvanceBtn = (enabled: boolean): React.CSSProperties => ({
  padding: '10px 18px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 13,
  cursor: enabled ? 'pointer' : 'not-allowed', background: enabled ? '#10B981' : '#334155',
  color: enabled ? '#fff' : '#64748b', transition: 'all 0.2s',
});
const styleProgressFill = (pct: number): React.CSSProperties => ({
  height: '100%', width: `${pct}%`, background: '#10B981', transition: 'width 0.3s',
});

const s: Record<string, React.CSSProperties> = {
  root: { display: 'flex', minHeight: '100vh', background: '#0F1923', color: '#E5EDF5' },
  sidebar: {
    width: 220, background: '#1A2B3C', borderRight: '1px solid #2D4259',
    padding: '20px 0', display: 'flex', flexDirection: 'column', gap: 2,
  },
  navItem: {
    padding: '10px 20px', fontSize: 13, cursor: 'pointer', display: 'flex',
    alignItems: 'center', gap: 10, color: '#8FA8C0', borderLeft: '3px solid transparent',
  },
  navItemActive: { background: '#243447', color: '#fff', borderLeft: '3px solid #4A7EB5' },
  main: { flex: 1, padding: 24, overflow: 'auto' },
  topbar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 20, gap: 16, flexWrap: 'wrap' as const,
  },
  titleBlock: { display: 'flex', flexDirection: 'column', gap: 4 },
  title: { fontSize: 22, fontWeight: 800, margin: 0, color: '#fff' },
  subtitle: { fontSize: 13, color: '#8FA8C0' },
  stageBadge: {
    padding: '6px 14px', borderRadius: 20, fontWeight: 700, fontSize: 12,
    color: '#fff', textTransform: 'uppercase' as const, letterSpacing: 0.5,
  },
  actionBar: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' as const },
  select: {
    padding: '8px 12px', borderRadius: 8, background: '#243447',
    border: '1px solid #2D4259', color: '#E5EDF5', fontSize: 13,
  },
  card: { background: '#1A2B3C', borderRadius: 10, padding: 18, marginBottom: 16 },
  cardTitle: { fontSize: 14, fontWeight: 700, color: '#fff', margin: 0, marginBottom: 12 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
  kv: { display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 8 },
  k: { fontSize: 11, color: '#8FA8C0', textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  v: { fontSize: 14, color: '#E5EDF5' },
  progressWrap: { background: '#243447', borderRadius: 6, height: 8, overflow: 'hidden' },
  checkRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #243447' },
  checkbox: { width: 18, height: 18, cursor: 'pointer', accentColor: '#10B981' },
  checkLabel: { fontSize: 14, flex: 1 },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '10px 12px', color: '#8FA8C0', fontWeight: 700, fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: 0.5, borderBottom: '1px solid #2D4259' },
  td: { padding: '10px 12px', borderBottom: '1px solid #243447', color: '#E5EDF5' },
  primaryBtn: { padding: '8px 14px', background: '#4A7EB5', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' },
  ghostBtn: { padding: '6px 12px', background: 'transparent', color: '#8FA8C0', border: '1px solid #2D4259', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  empty: { textAlign: 'center' as const, color: '#526980', padding: 32, fontStyle: 'italic' as const },
  input: {
    padding: '8px 12px', borderRadius: 6, background: '#243447',
    border: '1px solid #2D4259', color: '#E5EDF5', fontSize: 13, width: '100%',
    boxSizing: 'border-box' as const,
  },
  timelineItem: { display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid #243447' },
  loading: { padding: 40, textAlign: 'center' as const, color: '#8FA8C0' },
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const stageMeta = (k: string) => STAGES.find((x) => x.key === k) || STAGES[0];
const fmt = (d?: string | null) => { try { return d ? format(parseISO(d), 'MMM d, yyyy') : '—'; } catch { return '—'; } };
const money = (n?: number | null) => (n == null ? '—' : `$${Math.round(n).toLocaleString()}`);
const isAdmin = () => {
  try {
    const u = readUser();
    return u?.role === 'admin';
  } catch { return false; }
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const GovConstructionJobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [job, setJob] = useState<Job | null>(null);
  const [solicitation, setSolicitation] = useState<Solicitation | null>(null);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [subview, setSubview] = useState<SubView>('overview');

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/gov/construction-jobs/${id}`);
      setJob(data?.construction_job || null);
      setSolicitation(data?.solicitation || null);
      setActivity(data?.activity || []);
    } catch (e) {
      console.error('Failed to load construction job:', e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={s.root}><div style={s.main}><div style={s.loading}>Loading…</div></div></div>;
  if (!job) return <div style={s.root}><div style={s.main}><div style={s.empty}>Job not found</div></div></div>;

  const currentStage = stageMeta(job.stage);
  const stageIdx = STAGES.findIndex((x) => x.key === job.stage);
  const nextStage = STAGES[stageIdx + 1];

  const stageChecklist = CHECKLISTS[job.stage] || [];
  const stageState = (job.checklist_state && job.checklist_state[job.stage]) || {};
  const checkedCount = stageChecklist.filter((c) => !!stageState[c.key]).length;
  const complete = checkedCount === stageChecklist.length && stageChecklist.length > 0;
  const canAdvance = complete && !!nextStage;

  const toggleCheck = async (k: string, checked: boolean) => {
    if (!id) return;
    // optimistic
    setJob((j) => {
      if (!j) return j;
      const cs = { ...(j.checklist_state || {}) };
      if (!cs[j.stage]) cs[j.stage] = {};
      cs[j.stage] = { ...cs[j.stage], [k]: checked };
      return { ...j, checklist_state: cs };
    });
    try {
      await api.patch(`/gov/construction-jobs/${id}/checklist`, { stage: job.stage, key: k, checked });
    } catch (e) {
      console.error('checklist update failed', e);
      load();
    }
  };

  const doAdvance = async (to: string) => {
    if (!id) return;
    try {
      await api.post(`/gov/construction-jobs/${id}/advance`, { to_stage: to });
      load();
    } catch (e: any) {
      const resp = e?.response?.data;
      if (resp?.checklist_incomplete) {
        const unmet = resp.unmet_items || [];
        alert(`Cannot advance — checklist incomplete:\n• ${unmet.join('\n• ')}`);
      } else {
        alert(`Advance failed: ${resp?.error || e?.message || 'unknown'}`);
      }
    }
  };

  const forceStage = async (to: string) => {
    if (!id) return;
    if (!isAdmin()) { alert('Only admins can jump stages.'); return; }
    try {
      await api.post(`/gov/construction-jobs/${id}/advance?force=true`, { to_stage: to });
      load();
    } catch (e: any) {
      alert(`Failed: ${e?.response?.data?.error || e?.message}`);
    }
  };

  return (
    <div style={s.root}>
      {/* Left nav */}
      <aside style={s.sidebar}>
        <div style={{ padding: '0 20px 14px', borderBottom: '1px solid #2D4259', marginBottom: 10 }}>
          <button style={{ ...s.ghostBtn, width: '100%' }} onClick={() => nav('/gov/construction')}>← Back to Board</button>
        </div>
        {SUBVIEWS.map((v) => (
          <div
            key={v.key}
            style={{ ...s.navItem, ...(subview === v.key ? s.navItemActive : {}) }}
            onClick={() => setSubview(v.key)}
          >
            <span>{v.icon}</span> {v.label}
          </div>
        ))}
      </aside>

      {/* Main */}
      <div style={s.main}>
        {/* Top bar */}
        <div style={s.topbar}>
          <div style={s.titleBlock}>
            <h1 style={s.title}>{job.contract_number || 'Contract # pending'}</h1>
            <div style={s.subtitle}>
              {solicitation?.title || 'Untitled contract'} · {solicitation?.agency_name || 'Agency TBD'}
              {job.award_amount != null && ` · ${money(job.award_amount)}`}
            </div>
          </div>
          <div style={s.actionBar}>
            <span style={{ ...s.stageBadge, background: currentStage.color }}>{currentStage.label}</span>
            {nextStage && (
              <button
                disabled={!canAdvance}
                style={styleAdvanceBtn(canAdvance)}
                onClick={() => doAdvance(nextStage.key)}
                title={canAdvance ? '' : 'Complete the checklist to advance'}
              >
                → {nextStage.label}
              </button>
            )}
            <select
              style={s.select}
              value={job.stage}
              onChange={(e) => forceStage(e.target.value)}
              disabled={!isAdmin()}
              title={isAdmin() ? 'Admin: jump to any stage' : 'Admin only'}
            >
              {STAGES.map((st) => (<option key={st.key} value={st.key}>{st.label}</option>))}
            </select>
          </div>
        </div>

        {/* Sub-views */}
        {subview === 'overview' && (
          <Overview
            job={job}
            solicitation={solicitation}
            activity={activity}
            stageIdx={stageIdx}
            stageChecklist={stageChecklist}
            stageState={stageState}
            checkedCount={checkedCount}
            toggleCheck={toggleCheck}
          />
        )}
        {subview === 'documents' && <ConstructionDocuments solicitationId={solicitation?.id} />}
        {subview === 'financials' && <FinancialsView job={job} jobId={id!} />}
        {subview === 'daily_logs' && <DailyLogsView jobId={id!} />}
        {subview === 'personnel' && <PersonnelView job={job} jobId={id!} onReload={load} />}
        {subview === 'punch_list' && <PunchListView jobId={id!} />}
        {subview === 'rfis' && <RfisView jobId={id!} />}
        {subview === 'change_orders' && <ChangeOrdersView jobId={id!} />}
        {subview === 'submittals' && <SubmittalsView jobId={id!} />}
        {subview === 'certified_payroll' && <CertifiedPayrollView jobId={id!} />}
        {subview === 'contacts' && <ContactsView jobId={id!} />}
        {subview === 'photos' && <div style={{ ...s.card, ...s.empty }}>📸 Photo gallery coming soon.<br /><br />For now, upload photos via the Documents tab with type "photo".</div>}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Overview
// ─────────────────────────────────────────────────────────────────────────────
const Overview: React.FC<{
  job: Job; solicitation: Solicitation | null; activity: Activity[];
  stageIdx: number; stageChecklist: {key:string;label:string}[]; stageState: Record<string, boolean>;
  checkedCount: number; toggleCheck: (k: string, checked: boolean) => void;
}> = ({ job, solicitation, activity, stageIdx, stageChecklist, stageState, checkedCount, toggleCheck }) => {
  const pct = STAGES.length ? Math.round(((stageIdx + 1) / STAGES.length) * 100) : 0;
  return (
    <>
      {/* Stage progress */}
      <div style={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={s.cardTitle}>Stage {stageIdx + 1} of {STAGES.length}</div>
          <div style={{ color: '#8FA8C0', fontSize: 13 }}>{pct}% through pipeline</div>
        </div>
        <div style={s.progressWrap}><div style={styleProgressFill(pct)} /></div>
      </div>

      {/* Current stage checklist */}
      <div style={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={s.cardTitle}>Mandatory Checklist</div>
          <div style={{ color: '#8FA8C0', fontSize: 13 }}>{checkedCount} / {stageChecklist.length}</div>
        </div>
        {stageChecklist.length === 0 && <div style={s.empty}>No checklist required for this stage.</div>}
        {stageChecklist.map((c) => (
          <div key={c.key} style={s.checkRow}>
            <input
              type="checkbox"
              style={s.checkbox}
              checked={!!stageState[c.key]}
              onChange={(e) => toggleCheck(c.key, e.target.checked)}
            />
            <div style={s.checkLabel}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* Contract info */}
      <div style={s.card}>
        <div style={s.cardTitle}>Contract Info</div>
        <div style={s.grid2}>
          <div style={s.kv}><div style={s.k}>Contract #</div><div style={s.v}>{job.contract_number || '—'}</div></div>
          <div style={s.kv}><div style={s.k}>Task Order #</div><div style={s.v}>{job.task_order_number || '—'}</div></div>
          <div style={s.kv}><div style={s.k}>Contract Type</div><div style={s.v}>{job.contract_type || '—'}</div></div>
          <div style={s.kv}><div style={s.k}>Award Amount</div><div style={s.v}>{money(job.award_amount)}</div></div>
          <div style={s.kv}><div style={s.k}>Award Date</div><div style={s.v}>{fmt(job.award_date)}</div></div>
          <div style={s.kv}><div style={s.k}>PoP Start</div><div style={s.v}>{fmt(job.period_of_performance_start)}</div></div>
          <div style={s.kv}><div style={s.k}>PoP End</div><div style={s.v}>{fmt(job.period_of_performance_end)}</div></div>
          <div style={s.kv}><div style={s.k}>NTP Received</div><div style={s.v}>{fmt(job.ntp_received_at)}</div></div>
          <div style={s.kv}><div style={s.k}>Substantial Completion</div><div style={s.v}>{fmt(job.substantial_completion_date)}</div></div>
          <div style={s.kv}><div style={s.k}>Warranty End</div><div style={s.v}>{fmt(job.warranty_end_date)}</div></div>
          <div style={s.kv}><div style={s.k}>Job Site</div><div style={s.v}>{job.job_site_address || '—'}</div></div>
          <div style={s.kv}><div style={s.k}>% Complete</div><div style={s.v}>{job.percent_complete ?? 0}%</div></div>
        </div>
      </div>

      {/* Gov POCs */}
      <div style={s.card}>
        <div style={s.cardTitle}>Government POCs</div>
        <div style={s.grid2}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#8FA8C0', marginBottom: 6 }}>Contracting Officer</div>
            <div style={s.v}>{job.contracting_officer_name || '—'}</div>
            <div style={{ color: '#8FA8C0', fontSize: 13 }}>{job.contracting_officer_email || '—'}</div>
            <div style={{ color: '#8FA8C0', fontSize: 13 }}>{job.contracting_officer_phone || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#8FA8C0', marginBottom: 6 }}>COR</div>
            <div style={s.v}>{job.cor_name || '—'}</div>
            <div style={{ color: '#8FA8C0', fontSize: 13 }}>{job.cor_email || '—'}</div>
            <div style={{ color: '#8FA8C0', fontSize: 13 }}>{job.cor_phone || '—'}</div>
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div style={s.card}>
        <div style={s.cardTitle}>Recent Activity</div>
        {activity.length === 0 && <div style={s.empty}>No activity yet.</div>}
        {activity.slice(0, 5).map((a) => (
          <div key={a.id} style={s.timelineItem}>
            <div style={{ minWidth: 90, color: '#8FA8C0', fontSize: 12 }}>{fmt(a.created_at)}</div>
            <div>
              <div style={{ fontWeight: 600 }}>{a.action.replace(/_/g, ' ')}</div>
              {a.message && <div style={{ color: '#8FA8C0', fontSize: 13 }}>{a.message}</div>}
              {a.from_stage && a.to_stage && <div style={{ color: '#8FA8C0', fontSize: 12 }}>{a.from_stage} → {a.to_stage}</div>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Financials
// ─────────────────────────────────────────────────────────────────────────────
const FinancialsView: React.FC<{ job: Job; jobId: string }> = ({ job, jobId }) => {
  const [payApps, setPayApps] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<any>({ pay_app_number: 1, status: 'draft' });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/gov/construction-jobs/${jobId}/pay-applications`);
      setPayApps(data?.items || []);
    } catch (e) { console.error(e); }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post(`/gov/construction-jobs/${jobId}/pay-applications`, form);
      setShowForm(false); setForm({ pay_app_number: (payApps.length || 0) + 1, status: 'draft' });
      load();
    } catch (e: any) { alert(`Failed: ${e?.response?.data?.error || e?.message}`); }
  };

  const modsTotal = 0; // TODO could sum from change orders
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Contract Value', val: money(job.award_amount) },
          { label: 'Modifications', val: money(modsTotal) },
          { label: 'Invoiced', val: money(job.invoiced_to_date) },
          { label: 'Paid', val: money(job.paid_to_date) },
          { label: 'Retention Held', val: money(job.retention_held) },
          { label: '% Complete', val: `${job.percent_complete ?? 0}%` },
        ].map((c) => (
          <div key={c.label} style={{ ...s.card, marginBottom: 0 }}>
            <div style={s.k}>{c.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginTop: 6 }}>{c.val}</div>
          </div>
        ))}
      </div>

      <div style={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={s.cardTitle}>Pay Applications</div>
          <button style={s.primaryBtn} onClick={() => setShowForm(true)}>+ New Pay App</button>
        </div>
        {showForm && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 14, padding: 14, background: '#243447', borderRadius: 8 }}>
            <input style={s.input} placeholder="Pay App #" type="number" value={form.pay_app_number || ''} onChange={(e) => setForm({ ...form, pay_app_number: parseInt(e.target.value) || 0 })} />
            <input style={s.input} placeholder="Period Start" type="date" value={form.period_start || ''} onChange={(e) => setForm({ ...form, period_start: e.target.value })} />
            <input style={s.input} placeholder="Period End" type="date" value={form.period_end || ''} onChange={(e) => setForm({ ...form, period_end: e.target.value })} />
            <input style={s.input} placeholder="Amount Requested" type="number" value={form.amount_requested || ''} onChange={(e) => setForm({ ...form, amount_requested: parseFloat(e.target.value) || 0 })} />
            <button style={s.primaryBtn} onClick={create}>Save</button>
            <button style={s.ghostBtn} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        )}
        {payApps.length === 0 && !showForm && <div style={s.empty}>No pay applications yet.</div>}
        {payApps.length > 0 && (
          <table style={s.table}>
            <thead><tr>
              <th style={s.th}>#</th><th style={s.th}>Period</th><th style={s.th}>Requested</th>
              <th style={s.th}>Approved</th><th style={s.th}>Paid</th><th style={s.th}>Status</th>
            </tr></thead>
            <tbody>
              {payApps.map((p) => (
                <tr key={p.id}>
                  <td style={s.td}>{p.pay_app_number}</td>
                  <td style={s.td}>{fmt(p.period_start)} → {fmt(p.period_end)}</td>
                  <td style={s.td}>{money(p.amount_requested)}</td>
                  <td style={s.td}>{money(p.approved_amount)}</td>
                  <td style={s.td}>{money(p.paid_amount)}</td>
                  <td style={s.td}>{p.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Daily Logs
// ─────────────────────────────────────────────────────────────────────────────
const DailyLogsView: React.FC<{ jobId: string }> = ({ jobId }) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<any>({ log_date: new Date().toISOString().slice(0, 10) });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/gov/construction-jobs/${jobId}/daily-logs`);
      setLogs(data?.items || []);
    } catch (e) { console.error(e); }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post(`/gov/construction-jobs/${jobId}/daily-logs`, form);
      setShowForm(false); setForm({ log_date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (e: any) { alert(`Failed: ${e?.response?.data?.error || e?.message}`); }
  };

  return (
    <div style={s.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={s.cardTitle}>Daily Logs</div>
        <button style={s.primaryBtn} onClick={() => setShowForm(true)}>+ New Log</button>
      </div>
      {showForm && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14, padding: 14, background: '#243447', borderRadius: 8 }}>
          <input style={s.input} type="date" value={form.log_date || ''} onChange={(e) => setForm({ ...form, log_date: e.target.value })} />
          <input style={s.input} placeholder="Crew count" type="number" value={form.crew_count || ''} onChange={(e) => setForm({ ...form, crew_count: parseInt(e.target.value) || 0 })} />
          <input style={s.input} placeholder="Safety topic" value={form.safety_topic || ''} onChange={(e) => setForm({ ...form, safety_topic: e.target.value })} />
          <textarea style={{ ...s.input, gridColumn: 'span 3', minHeight: 80 }} placeholder="Work performed…" value={form.work_performed || ''} onChange={(e) => setForm({ ...form, work_performed: e.target.value })} />
          <button style={s.primaryBtn} onClick={create}>Save</button>
          <button style={s.ghostBtn} onClick={() => setShowForm(false)}>Cancel</button>
        </div>
      )}
      {logs.length === 0 && !showForm && <div style={s.empty}>No daily logs yet.</div>}
      {logs.length > 0 && (
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Date</th><th style={s.th}>Crew</th><th style={s.th}>Work Performed</th><th style={s.th}>Safety</th>
          </tr></thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id}>
                <td style={s.td}>{fmt(l.log_date)}</td>
                <td style={s.td}>{l.crew_count ?? '—'}</td>
                <td style={s.td}>{(l.work_performed || '').slice(0, 80)}</td>
                <td style={s.td}>{l.safety_topic || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Personnel — writes back to the parent job record via PATCH
// ─────────────────────────────────────────────────────────────────────────────
const PersonnelView: React.FC<{ job: Job; jobId: string; onReload: () => void }> = ({ job, jobId, onReload }) => {
  const [users, setUsers] = useState<any[]>([]);
  useEffect(() => {
    (async () => { try { const { data } = await api.get('/users'); setUsers(Array.isArray(data) ? data : data?.users || []); } catch { /* no users */ } })();
  }, []);
  const update = async (field: string, val: string) => {
    try { await api.patch(`/gov/construction-jobs/${jobId}`, { [field]: val || null }); onReload(); }
    catch (e: any) { alert(`Failed: ${e?.response?.data?.error || e?.message}`); }
  };
  return (
    <div style={s.card}>
      <div style={s.cardTitle}>Assigned Personnel</div>
      <div style={s.grid2}>
        {[
          { field: 'project_manager_id', label: 'Project Manager', val: job.project_manager_id },
          { field: 'assigned_super_id', label: 'Superintendent', val: job.assigned_super_id },
          { field: 'assigned_foreman_id', label: 'Foreman', val: job.assigned_foreman_id },
        ].map((row) => (
          <div key={row.field} style={s.kv}>
            <div style={s.k}>{row.label}</div>
            <select style={s.select} value={row.val || ''} onChange={(e) => update(row.field, e.target.value)}>
              <option value="">— Unassigned —</option>
              {users.map((u) => (<option key={u.id} value={u.id}>{u.name || u.email}</option>))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Generic sub-list (used for punch list, RFIs, change orders, submittals, cp, contacts)
// ─────────────────────────────────────────────────────────────────────────────
const SubList: React.FC<{
  jobId: string;
  resource: string; // e.g. "rfis"
  title: string;
  columns: string[];
  formFields?: { key: string; label: string; type?: string; placeholder?: string; options?: { value: string; label: string }[] }[];
}> = ({ jobId, resource, title, columns, formFields }) => {
  const [items, setItems] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<any>({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/gov/construction-jobs/${jobId}/${resource}`);
      setItems(data?.items || data?.documents || []);
    } catch (e) { console.error(`load ${resource}`, e); }
  }, [jobId, resource]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post(`/gov/construction-jobs/${jobId}/${resource}`, form);
      setShowForm(false); setForm({});
      load();
    } catch (e: any) { alert(`Failed: ${e?.response?.data?.error || e?.message}`); }
  };

  return (
    <div style={s.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={s.cardTitle}>{title}</div>
        {formFields && <button style={s.primaryBtn} onClick={() => setShowForm(true)}>+ New</button>}
      </div>
      {showForm && formFields && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14, padding: 14, background: '#243447', borderRadius: 8 }}>
          {formFields.map((f) => (
            f.options ? (
              <select key={f.key} style={s.select} value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}>
                <option value="">— {f.label} —</option>
                {f.options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
              </select>
            ) : (
              <input
                key={f.key} style={s.input}
                type={f.type || 'text'}
                placeholder={f.placeholder || f.label}
                value={form[f.key] || ''}
                onChange={(e) => setForm({ ...form, [f.key]: f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value })}
              />
            )
          ))}
          <button style={s.primaryBtn} onClick={create}>Save</button>
          <button style={s.ghostBtn} onClick={() => { setShowForm(false); setForm({}); }}>Cancel</button>
        </div>
      )}
      {items.length === 0 && !showForm && <div style={s.empty}>No {title.toLowerCase()} yet.</div>}
      {items.length > 0 && (
        <table style={s.table}>
          <thead><tr>{columns.map((c) => (<th key={c} style={s.th}>{c.replace(/_/g, ' ')}</th>))}</tr></thead>
          <tbody>
            {items.map((it: any) => (
              <tr key={it.id}>{columns.map((c) => (
                <td key={c} style={s.td}>
                  {c.includes('date') || c.includes('_at') ? fmt(it[c])
                    : (typeof it[c] === 'number' && c.includes('amount')) ? money(it[c])
                    : String(it[c] ?? '—').slice(0, 80)}
                </td>
              ))}</tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// Purpose-built shortcuts
const PunchListView: React.FC<{ jobId: string }> = ({ jobId }) => (
  <SubList jobId={jobId} resource="punch-list" title="Punch List Items"
    columns={['item_number', 'location', 'description', 'responsible_trade', 'status']}
    formFields={[
      { key: 'item_number', label: 'Item #', type: 'number' },
      { key: 'location', label: 'Location' },
      { key: 'description', label: 'Description' },
      { key: 'responsible_trade', label: 'Trade' },
      { key: 'status', label: 'Status', options: [
        { value: 'open', label: 'Open' }, { value: 'in_progress', label: 'In Progress' },
        { value: 'complete', label: 'Complete' }, { value: 'verified', label: 'Verified' },
      ]},
    ]}
  />
);
const RfisView: React.FC<{ jobId: string }> = ({ jobId }) => (
  <SubList jobId={jobId} resource="rfis" title="RFIs"
    columns={['rfi_number', 'subject', 'status', 'priority', 'submitted_at']}
    formFields={[
      { key: 'rfi_number', label: 'RFI #' },
      { key: 'subject', label: 'Subject' },
      { key: 'question', label: 'Question' },
      { key: 'priority', label: 'Priority', options: [
        { value: 'low', label: 'Low' }, { value: 'normal', label: 'Normal' },
        { value: 'high', label: 'High' }, { value: 'urgent', label: 'Urgent' },
      ]},
      { key: 'status', label: 'Status', options: [
        { value: 'open', label: 'Open' }, { value: 'answered', label: 'Answered' }, { value: 'closed', label: 'Closed' },
      ]},
    ]}
  />
);
const ChangeOrdersView: React.FC<{ jobId: string }> = ({ jobId }) => (
  <SubList jobId={jobId} resource="change-orders" title="Change Orders"
    columns={['change_order_number', 'modification_number', 'description', 'proposed_amount', 'approved_amount', 'days_added', 'status']}
    formFields={[
      { key: 'change_order_number', label: 'CO #' },
      { key: 'modification_number', label: 'Mod #' },
      { key: 'description', label: 'Description' },
      { key: 'proposed_amount', label: 'Proposed $', type: 'number' },
      { key: 'days_added', label: 'Days Added', type: 'number' },
      { key: 'status', label: 'Status', options: [
        { value: 'draft', label: 'Draft' }, { value: 'submitted', label: 'Submitted' },
        { value: 'negotiating', label: 'Negotiating' }, { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' }, { value: 'executed', label: 'Executed' },
      ]},
    ]}
  />
);
const SubmittalsView: React.FC<{ jobId: string }> = ({ jobId }) => (
  <SubList jobId={jobId} resource="submittals" title="Submittals"
    columns={['submittal_number', 'spec_section', 'description', 'submittal_type', 'status', 'long_lead']}
    formFields={[
      { key: 'submittal_number', label: 'Submittal #' },
      { key: 'spec_section', label: 'Spec Section' },
      { key: 'description', label: 'Description' },
      { key: 'submittal_type', label: 'Type', options: [
        { value: 'shop_drawing', label: 'Shop Drawing' }, { value: 'product_data', label: 'Product Data' },
        { value: 'sample', label: 'Sample' }, { value: 'certificate', label: 'Certificate' },
        { value: 'o_and_m', label: 'O&M Manual' },
      ]},
      { key: 'status', label: 'Status', options: [
        { value: 'pending', label: 'Pending' }, { value: 'submitted', label: 'Submitted' },
        { value: 'under_review', label: 'Under Review' }, { value: 'approved', label: 'Approved' },
        { value: 'approved_as_noted', label: 'Approved as Noted' }, { value: 'resubmit', label: 'Resubmit' },
        { value: 'rejected', label: 'Rejected' },
      ]},
    ]}
  />
);
const CertifiedPayrollView: React.FC<{ jobId: string }> = ({ jobId }) => (
  <SubList jobId={jobId} resource="certified-payroll" title="Certified Payroll (WH-347)"
    columns={['week_ending', 'payroll_number', 'submitted_at', 'compliance_verified']}
    formFields={[
      { key: 'week_ending', label: 'Week Ending', type: 'date' },
      { key: 'payroll_number', label: 'Payroll #', type: 'number' },
      { key: 'file_url', label: 'WH-347 File URL' },
    ]}
  />
);
const ContactsView: React.FC<{ jobId: string }> = ({ jobId }) => (
  <SubList jobId={jobId} resource="contacts" title="Contacts"
    columns={['contact_type', 'name', 'organization', 'email', 'phone']}
    formFields={[
      { key: 'contact_type', label: 'Type', options: [
        { value: 'co', label: 'Contracting Officer' }, { value: 'cor', label: 'COR' },
        { value: 'sub', label: 'Subcontractor' }, { value: 'inspector', label: 'Inspector' },
        { value: 'supplier', label: 'Supplier' }, { value: 'other', label: 'Other' },
      ]},
      { key: 'name', label: 'Name' },
      { key: 'organization', label: 'Organization' },
      { key: 'title', label: 'Title' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
    ]}
  />
);

export default GovConstructionJobDetailPage;

function ConstructionDocuments({ solicitationId }: { solicitationId?: string }) {
  const [documents, setDocuments] = useState<any[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const loadDocuments = useCallback(async () => {
    if (!solicitationId) return;
    setError('');
    try {
      const { data } = await api.get(`/gov/solicitations/${solicitationId}/documents`);
      setDocuments(data.documents || []);
    } catch { setError('Unable to load documents. Please retry.'); }
    finally { setLoading(false); }
  }, [solicitationId]);
  useEffect(() => { void loadDocuments(); }, [loadDocuments]);
  if (!solicitationId) return <div style={s.card}>Link this construction job to a solicitation to manage its documents.</div>;
  return <div>
    <div style={{ ...s.subtitle, marginBottom: 16 }}>Documents are shared with the linked solicitation.</div>
    {loading && <div role="status">Loading documents…</div>}
    {error && <div role="alert">{error} <button style={s.ghostBtn} onClick={loadDocuments}>Retry</button></div>}
    <DocumentsTab solicitationId={solicitationId} documents={documents} onReload={loadDocuments} />
  </div>;
}
